import { DialogComponent } from './dialog.js';
import { Component } from './component.js';

class WishlistManager {
  constructor() {
    this.storageKey = 'shopify-wishlist';
    this.items = this.load();
    this.listeners = [];
    // Initialize count on load
    setTimeout(() => this.notify(), 100);

    document.addEventListener('cart:update', () => {
      setTimeout(() => this.notify(), 100);
    });
  }

  load() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  save() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    this.notify();
  }

  add(productId, handle) {
    if (!this.items.find(item => item.id === productId)) {
      this.items.push({ id: productId, handle: handle });
      this.save();
    }
  }

  remove(productId) {
    this.items = this.items.filter(item => item.id !== productId);
    this.save();
  }

  toggle(productId, handle) {
    if (this.items.find(item => item.id === productId)) {
      this.remove(productId);
    } else {
      this.add(productId, handle);
    }
  }

  isInWishlist(productId) {
    return !!this.items.find(item => item.id === productId);
  }

  getCount() {
    return this.items.length;
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  notify() {
    const count = this.getCount();
    this.listeners.forEach(callback => callback(this.items, count));
    
    // Update all count bubbles
    const countBubbles = document.querySelectorAll('[data-wishlist-count]');
    countBubbles.forEach(bubble => {
      bubble.textContent = count;
      
      const container = bubble.closest('[data-wishlist-count-container]');
      if (container) {
        if (count > 0) {
          container.classList.remove('visually-hidden');
        } else {
          container.classList.add('visually-hidden');
        }
      }
    });

    document.dispatchEvent(new CustomEvent('wishlist:updated', { 
      detail: { items: this.items, count } 
    }));
  }
}

export const wishlist = new WishlistManager();

class WishlistComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.updateCount();
    this.renderItems();
    this.addEventListener('dialog:open', () => this.renderItems());
    this.unsubscribe = wishlist.subscribe(() => {
      this.updateCount();
      this.renderItems();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.unsubscribe) this.unsubscribe();
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  updateCount() {
    const count = wishlist.getCount();
    const bubbles = this.querySelectorAll('[data-wishlist-count]');
    bubbles.forEach(b => {
      b.textContent = count;
      b.hidden = count === 0;
    });
  }

  async renderItems() {
    const container = this.refs.itemsContainer;
    const tableBody = this.refs.itemsTableBody;
    const emptyState = this.refs.emptyState;
    
    if (!container || !tableBody || !emptyState) return;

    const items = wishlist.items;

    const isEmpty = items.length === 0;
    this.setAttribute('data-empty', isEmpty);

    if (isEmpty) {
      if (tableBody) tableBody.innerHTML = '';
      if (container) container.hidden = true;
      if (emptyState) emptyState.hidden = false;
      return;
    }

    container.hidden = false;
    emptyState.hidden = true;

    // Only render if items changed
    const currentIds = Array.from(tableBody.querySelectorAll('[data-product-id]')).map(el => el.dataset.productId);
    const newIds = items.map(item => String(item.id));
    
    if (JSON.stringify(currentIds) === JSON.stringify(newIds)) return;

    this.setAttribute('loading', 'true');

    try {
      const htmls = await Promise.all(items.map(async (item) => {
        const response = await fetch(`/products/${item.handle}?view=wishlist-item`);
        return await response.text();
      }));

      tableBody.innerHTML = htmls.join('');
    } catch (e) {
      console.error('Wishlist render error:', e);
    } finally {
      this.removeAttribute('loading');
    }
  }
}

if (!customElements.get('wishlist-component')) {
  customElements.define('wishlist-component', WishlistComponent);
}

class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (!this._boundToggle) {
      this._boundToggle = this.toggle.bind(this);
      this.addEventListener('click', this._boundToggle);
    }
    this.updateState();
    this.unsubscribe = wishlist.subscribe(() => this.updateState());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.unsubscribe) this.unsubscribe();
  }

  toggle(e) {
    e.preventDefault();
    e.stopPropagation();
    if (this.hasAttribute('loading')) return;

    const id = this.dataset.productId;
    const handle = this.dataset.productHandle;

    this.setAttribute('loading', 'true');
    this.classList.add('is-loading');

    wishlist.toggle(id, handle);
  }

  updateState() {
    const id = this.dataset.productId;
    const isActive = wishlist.isInWishlist(id);
    this.classList.toggle('is-active', isActive);
    this.removeAttribute('loading');
    this.classList.remove('is-loading');
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
